import type { ReactNode } from "react";
import { createBrowserRouter } from "react-router-dom";
import { AppShell, type NavGroup } from "./shared/components/AppShell";
import { FieldShell } from "./shared/components/FieldShell";
import { RequireAuth } from "./domains/auth/RequireAuth";
import { RequireRole } from "./domains/auth/RequireRole";
import { RequirePermission } from "./domains/auth/RequirePermission";

import { LoginScreen } from "./domains/auth/screens/LoginScreen";
import { ChangePassword } from "./domains/auth/screens/ChangePassword";

import { S1LeadCapture } from "./domains/field/screens/S1LeadCapture";
import { S2DuplicateCheck } from "./domains/field/screens/S2DuplicateCheck";
import { S3DuplicateReview } from "./domains/field/screens/S3DuplicateReview";
import { S4KycSubmission } from "./domains/field/screens/S4KycSubmission";
import { FieldLeadClosed, FieldLeadsHome } from "./domains/field/screens/FieldLeadsHome";

import { S5KycReviewQueue } from "./domains/office/screens/S5KycReviewQueue";
import { S6KycDecision } from "./domains/office/screens/S6KycDecision";

import { S1MyInstallations } from "./domains/installation/screens/S1MyInstallations";
import { S2ScanSerial } from "./domains/installation/screens/S2ScanSerial";
import { S3EvidenceCapture } from "./domains/installation/screens/S3EvidenceCapture";
import { S4PaymentCheck } from "./domains/installation/screens/S4PaymentCheck";
import { S5SubmitGate } from "./domains/installation/screens/S5SubmitGate";
import { S6KeyApprovalQueue } from "./domains/installation/screens/S6KeyApprovalQueue";
import { S7KeyApproval } from "./domains/installation/screens/S7KeyApproval";
import { ScheduleInstallation } from "./domains/installation/screens/ScheduleInstallation";

import { S1RecordPayment } from "./domains/payments/screens/S1RecordPayment";
import { S2VerificationResult } from "./domains/payments/screens/S2VerificationResult";
import { S3AllocationReceipt } from "./domains/payments/screens/S3AllocationReceipt";
import { S4TokenDelivery } from "./domains/payments/screens/S4TokenDelivery";
import { S5CustomerTimeline } from "./domains/payments/screens/S5CustomerTimeline";
import { S6ReconciliationQueue } from "./domains/payments/screens/S6ReconciliationQueue";
import { S7ExceptionResolve } from "./domains/payments/screens/S7ExceptionResolve";
import { PaymentApprovalQueue } from "./domains/payments/screens/PaymentApprovalQueue";
import { OverdueReminders } from "./domains/payments/screens/OverdueReminders";
import { Commissions } from "./domains/payments/screens/Commissions";

import { S8_dMRV_Dashboard } from "./domains/dmrv/screens/S8_dMRV_Dashboard";

import { DeviceDashboard } from "./domains/devices/screens/DeviceDashboard";
import { DeviceRegistration } from "./domains/devices/screens/DeviceRegistration";
import { DeviceDetail } from "./domains/devices/screens/DeviceDetail";
import { OpenPaygoExceptionsQueue } from "./domains/devices/screens/OpenPaygoExceptionsQueue";
import { ManufacturerCatalog } from "./domains/devices/screens/ManufacturerCatalog";

import { CustomerList } from "./domains/customers/screens/CustomerList";
import { CustomerDetail } from "./domains/customers/screens/CustomerDetail";
import { CustomerCreate } from "./domains/customers/screens/CustomerCreate";

import { BulkImport } from "./domains/bulk/screens/BulkImport";

import { SecurityGroups } from "./domains/admin/screens/SecurityGroups";
import { UserManagement } from "./domains/admin/screens/UserManagement";
import { TenantManagement } from "./domains/admin/screens/TenantManagement";
import { ApiKeyManagement } from "./domains/admin/screens/ApiKeyManagement";
import { AuditLogList } from "./domains/admin/screens/AuditLogList";
import { AreaManagement } from "./domains/admin/screens/AreaManagement";
import { DiscountRules } from "./domains/admin/screens/DiscountRules";
import { SystemHealth } from "./domains/admin/screens/SystemHealth";

import { ProductCatalogue } from "./domains/products/screens/ProductCatalogue";
import { BillingDashboard } from "./domains/billing/screens/BillingDashboard";
import { NotificationLog } from "./domains/notifications/screens/NotificationLog";
import { SmsCenter } from "./domains/notifications/screens/SmsCenter";
import { OperationsDashboard } from "./domains/dashboard/screens/OperationsDashboard";
import { AnalyticsDashboard } from "./domains/analytics/screens/AnalyticsDashboard";
import { CustomerMap } from "./domains/map/screens/CustomerMap";
import { TicketList } from "./domains/tickets/screens/TicketList";
import { VillageChairmanHome } from "./domains/vc/screens/VillageChairmanHome";
import { CustomerPortal } from "./domains/portal/screens/CustomerPortal";
import { RoleHomeRedirect } from "./domains/auth/RoleHomeRedirect";

const COMPANY_OPS = [
  "business_admin", "commercial_officer",
  "technician", "sales_rep", "m_and_e_officer", "rea_officer",
] as const;
/** Company admins only — no platform operator. */
const COMPANY_ADMIN = ["business_admin"] as const;
/** Platform operator — Tenants, Keys, Health. */
const SYS = ["system_admin"] as const;
/** Platform + company admin — Users, Security Groups, Audit. */
const GOV_ADMIN = ["system_admin", "business_admin"] as const;
const USER_ROLES = [...GOV_ADMIN, "sales_rep", "commercial_officer"] as const;
const FINANCE = ["business_admin", "commercial_officer"] as const;
const KYC_ROLES = ["business_admin", "commercial_officer"] as const;
const KEY_ROLES = ["business_admin", "commercial_officer"] as const;
const BULK_ROLES = ["business_admin", "commercial_officer", "sales_rep"] as const;
const DMRV_ROLES = [
  "business_admin", "commercial_officer",
  "technician", "sales_rep", "village_chairman", "m_and_e_officer", "rea_officer",
] as const;
const SALES_PRODUCTS = [...COMPANY_ADMIN, "commercial_officer", "sales_rep", "m_and_e_officer", "rea_officer"] as const;
const FIELD_SALES = ["business_admin", "commercial_officer", "sales_rep"] as const;
const FIELD_TECH = ["business_admin", "technician", "commercial_officer"] as const;
const ANALYTICS = ["business_admin", "commercial_officer", "m_and_e_officer", "rea_officer", "village_chairman"] as const;
const MAP_ROLES = ["business_admin", "commercial_officer", "technician", "sales_rep", "m_and_e_officer", "rea_officer", "village_chairman"] as const;
const TICKET_ROLES = ["business_admin", "commercial_officer", "technician", "m_and_e_officer"] as const;
const SMS_ROLES = [...FINANCE] as const;
const COMMISSION_ROLES = ["business_admin", "commercial_officer", "sales_rep", "technician"] as const;
const APPROVAL_ROLES = ["business_admin", "commercial_officer"] as const;
const CUSTOMER_VIEW = [...COMPANY_OPS, "village_chairman"] as const;
const ALL_STAFF = [...COMPANY_OPS, "village_chairman", "system_admin"] as const;

/**
 * Sidebar groups — every role story should have a visible entry here.
 * Empty groups (no visible items for the role) are hidden by AppShell.
 */
const nav: NavGroup[] = [
  {
    id: "workspace",
    label: "Workspace",
    items: [
      { to: "/", label: "Dashboard", roles: [...COMPANY_OPS], end: true },
      { to: "/admin/health", label: "Platform home", roles: [...SYS], end: true },
      { to: "/vc", label: "My Village", roles: ["village_chairman"] },
      { to: "/portal", label: "My Account", roles: ["customer"] },
    ],
  },
  {
    id: "field",
    label: "Field",
    items: [
      { to: "/field/leads", label: "Leads / KYC", roles: [...FIELD_SALES] },
      { to: "/field/installations", label: "My Installations", roles: [...FIELD_TECH] },
      { to: "/installations/new", label: "Schedule Installation", roles: [...COMPANY_ADMIN, "commercial_officer"] },
      { to: "/field/payments/new", label: "Payment Records", roles: [...FINANCE, "sales_rep"] },
    ],
  },
  {
    id: "sales",
    label: "Sales",
    items: [
      { to: "/customers", label: "Customers", roles: [...CUSTOMER_VIEW] },
      { to: "/bulk-import", label: "Bulk Import", roles: [...BULK_ROLES] },
      { to: "/kyc-review", label: "KYC Review", roles: [...KYC_ROLES], permissions: ["kyc.read"] },
      { to: "/products", label: "Products", roles: [...SALES_PRODUCTS] },
      { to: "/commissions", label: "Commissions", roles: [...COMMISSION_ROLES] },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    items: [
      { to: "/devices", label: "Devices", roles: [...COMPANY_OPS] },
      { to: "/devices/catalog", label: "Manufacturers / Types", roles: [...COMPANY_ADMIN] },
      { to: "/key-approvals", label: "Key Approvals (first token)", roles: [...KEY_ROLES] },
      { to: "/openpaygo-exceptions", label: "OpenPAYGO Exceptions", roles: [...APPROVAL_ROLES] },
      { to: "/dmrv", label: "dMRV", roles: [...DMRV_ROLES] },
      { to: "/tickets", label: "Support Tickets", roles: [...TICKET_ROLES] },
      { to: "/map", label: "Customer Map", roles: [...MAP_ROLES] },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    items: [
      { to: "/payment-approvals", label: "Payment Approvals (cash → token)", roles: [...APPROVAL_ROLES] },
      { to: "/overdue", label: "Overdue / Reminders", roles: [...FINANCE] },
      { to: "/reconciliation", label: "Reconciliation", roles: [...FINANCE] },
      { to: "/sms-center", label: "SMS Center", roles: [...SMS_ROLES], permissions: ["sms.send"] },
      { to: "/notifications", label: "Notification Log", roles: [...FINANCE] },
      { to: "/billing", label: "Billing", roles: [...COMPANY_ADMIN] },
      { to: "/discounts", label: "Discount Rules", roles: [...COMPANY_ADMIN] },
    ],
  },
  {
    id: "insight",
    label: "Insight",
    items: [
      { to: "/analytics", label: "Analytics", roles: [...ANALYTICS] },
      { to: "/admin/health", label: "System Health", roles: [...SYS] },
    ],
  },
  {
    id: "governance",
    label: "Governance",
    items: [
      { to: "/admin/users", label: "Users", roles: [...USER_ROLES], permissions: ["user.manage", "user.create_field"] },
      { to: "/admin/security-groups", label: "Security Groups", roles: [...GOV_ADMIN] },
      { to: "/admin/tenants", label: "Tenants", roles: [...SYS] },
      { to: "/admin/areas", label: "Areas", roles: [...COMPANY_ADMIN, "m_and_e_officer", "rea_officer"] },
      { to: "/admin/api-keys", label: "API Keys", roles: [...SYS] },
      { to: "/admin/audit-logs", label: "Audit Logs", roles: [...GOV_ADMIN] },
      { to: "/account/password", label: "Change Password", roles: [...ALL_STAFF, "customer"] },
    ],
  },
];

function gate(roles: readonly string[], el: ReactNode, permissions?: string[]) {
  const wrapped = permissions && permissions.length > 0
    ? <RequirePermission roles={[...roles]} permissions={permissions}>{el}</RequirePermission>
    : <RequireRole roles={[...roles]}>{el}</RequireRole>;
  return wrapped;
}

export const router = createBrowserRouter(
  [
    {
      path: "/login",
      element: <LoginScreen />,
    },
    {
      path: "/",
      element: (
        <RequireAuth>
          <AppShell nav={nav} />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <RoleHomeRedirect /> },

        { path: "portal", element: gate(["customer"], <CustomerPortal />) },
        { path: "vc", element: gate(["village_chairman"], <VillageChairmanHome />) },
        { path: "account/password", element: <ChangePassword /> },

        { path: "customers", element: gate(CUSTOMER_VIEW, <CustomerList />) },
        { path: "customers/new", element: gate(COMPANY_OPS, <CustomerCreate />) },
        { path: "customers/:id", element: gate(CUSTOMER_VIEW, <CustomerDetail />) },

        { path: "bulk-import", element: gate(BULK_ROLES, <BulkImport />) },

        { path: "kyc-review", element: gate(KYC_ROLES, <S5KycReviewQueue />) },
        { path: "kyc-review/:id", element: gate(KYC_ROLES, <S6KycDecision />) },

        { path: "key-approvals", element: gate(KEY_ROLES, <S6KeyApprovalQueue />) },
        { path: "key-approvals/:id", element: gate(KEY_ROLES, <S7KeyApproval />) },
        { path: "openpaygo-exceptions", element: gate(APPROVAL_ROLES, <OpenPaygoExceptionsQueue />) },
        { path: "installations/new", element: gate([...COMPANY_ADMIN, "commercial_officer"], <ScheduleInstallation />) },

        { path: "payment-approvals", element: gate(APPROVAL_ROLES, <PaymentApprovalQueue />) },
        { path: "overdue", element: gate(FINANCE, <OverdueReminders />) },
        { path: "commissions", element: gate(COMMISSION_ROLES, <Commissions />) },
        { path: "reconciliation", element: gate(FINANCE, <S6ReconciliationQueue />) },
        { path: "reconciliation/:id", element: gate(FINANCE, <S7ExceptionResolve />) },

        { path: "dmrv", element: gate(DMRV_ROLES, <S8_dMRV_Dashboard />) },
        { path: "tickets", element: gate(TICKET_ROLES, <TicketList />) },
        { path: "map", element: gate(MAP_ROLES, <CustomerMap />) },
        { path: "analytics", element: gate(ANALYTICS, <AnalyticsDashboard />) },

        { path: "devices", element: gate(COMPANY_OPS, <DeviceDashboard />) },
        { path: "devices/new", element: gate(COMPANY_OPS, <DeviceRegistration />) },
        { path: "devices/catalog", element: gate(COMPANY_ADMIN, <ManufacturerCatalog />) },
        { path: "devices/:serial", element: gate(COMPANY_OPS, <DeviceDetail />) },

        { path: "products", element: gate(SALES_PRODUCTS, <ProductCatalogue />) },
        { path: "billing", element: gate(COMPANY_ADMIN, <BillingDashboard />) },
        { path: "discounts", element: gate(COMPANY_ADMIN, <DiscountRules />) },
        { path: "sms-center", element: gate(SMS_ROLES, <SmsCenter />) },
        { path: "notifications", element: gate(FINANCE, <NotificationLog />) },

        { path: "admin/users", element: gate(USER_ROLES, <UserManagement />, ["user.manage", "user.create_field"]) },
        { path: "admin/security-groups", element: gate(GOV_ADMIN, <SecurityGroups />) },
        { path: "admin/tenants", element: gate(SYS, <TenantManagement />) },
        { path: "admin/api-keys", element: gate(SYS, <ApiKeyManagement />) },
        { path: "admin/areas", element: gate([...COMPANY_ADMIN, "m_and_e_officer", "rea_officer"], <AreaManagement />) },
        { path: "admin/audit-logs", element: gate(GOV_ADMIN, <AuditLogList />) },
        { path: "admin/health", element: gate(SYS, <SystemHealth />) },

        // Ops dashboard still reachable for company roles that land elsewhere by default
        { path: "dashboard", element: gate(COMPANY_OPS, <OperationsDashboard />) },
      ],
    },
    {
      path: "/field",
      element: (
        <RequireAuth>
          <FieldShell />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <RoleHomeRedirect /> },
        { path: "leads", element: gate(FIELD_SALES, <FieldLeadsHome />) },
        { path: "leads/new", element: gate(FIELD_SALES, <S1LeadCapture />) },
        { path: "leads/:id/duplicates", element: gate(FIELD_SALES, <S2DuplicateCheck />) },
        { path: "leads/:id/duplicates/:candidateId", element: gate(FIELD_SALES, <S3DuplicateReview />) },
        { path: "leads/:id/kyc", element: gate(FIELD_SALES, <S4KycSubmission />) },
        { path: "leads/:id/closed", element: gate(FIELD_SALES, <FieldLeadClosed />) },

        { path: "installations", element: gate(FIELD_TECH, <S1MyInstallations />) },
        { path: "installations/:id/scan", element: gate(FIELD_TECH, <S2ScanSerial />) },
        { path: "installations/:id/evidence", element: gate(FIELD_TECH, <S3EvidenceCapture />) },
        { path: "installations/:id/payment", element: gate(FIELD_TECH, <S4PaymentCheck />) },
        { path: "installations/:id/submit", element: gate(FIELD_TECH, <S5SubmitGate />) },

        { path: "payments/new", element: gate([...FINANCE, "sales_rep"], <S1RecordPayment />) },
        { path: "payments/:id/verification", element: gate([...FINANCE, "sales_rep"], <S2VerificationResult />) },
        { path: "payments/:id/allocation", element: gate([...FINANCE, "sales_rep"], <S3AllocationReceipt />) },
        { path: "payments/:id/token", element: gate([...FINANCE, "sales_rep"], <S4TokenDelivery />) },
        { path: "customers/:id/payment-timeline", element: gate([...FINANCE, "sales_rep"], <S5CustomerTimeline />) },
      ],
    },
  ],
  { basename: "/app" },
);
